// LaTeX -> spoken Swedish, for the text-to-speech feature.
//
// Deliberately partial. Notation that has no good linear reading (integrals,
// matrices, sums) returns the single word "uttrycket" instead of reading
// symbol soup aloud — a student who hears that knows to look at the screen.
// The SAME fallback fires for any individual \command this file does not
// recognise (see OkantKommandoError below): silence would let a student
// hear a fluent but false sentence (e.g. "x = 2 \Rightarrow y = 4" losing
// the arrow and becoming two flatly-joined statements), which is worse than
// an honest "look at the screen" signal. Only a small, explicit allowlist of
// commands that truly carry no spoken meaning (INGEN_INNEBORD, plus the
// \big-family regex) is allowed to render as silence.
// Everything the translator does claim to handle is pinned by tests in
// latex-till-tal.test.ts.

const FALLBACK = "uttrycket";

// Thrown from deep inside tolka()'s recursion (nested {...} groups, \frac
// arguments, ...) when an unrecognised \command is met. Caught once, at the
// top in latexTillTal(), to fall back for the WHOLE expression — the same
// "bail out entirely" policy EJ_STODD already applies up front, extended to
// individual unknown tokens instead of only a fixed list of command names.
class OkantKommandoError extends Error {}

// If any of these appear anywhere in the expression we bail out entirely:
// their internal structure cannot be read left-to-right.
// Note: (?![a-zA-Z]) instead of \b — LaTeX commands are immediately followed
// by things like "_" (subscript) or "{" that \b treats as word characters
// too, so \b would miss "\int_0^1".
const EJ_STODD =
  /\\(begin|iint|oint|prod|lim|substack|matrix|cases|overbrace|overset|underset)(?![a-zA-Z])/;

const KOMMANDON: Record<string, string> = {
  // Platshållare i "fyll i rutan"-uppgifter. Utan den här raden faller hela
  // uttrycket tillbaka på ordet "uttrycket", och en elev som lyssnar hör
  // "uttrycket" i stället för "nio gånger rutan är lika med sjuttiotvå".
  // (Åk 7-svepet 2026-08-22: 50 sådana uttryck i GRMAT700X.)
  "\\square": " rutan ",
  "\\Box": " rutan ",
  // Mängdklamrar läses inte ut: "$\\{1, 2, 3, 6\\}$" blir "1, 2, 3, 6", precis
  // som en lärare läser upp en faktorlista. Att säga "måsvinge" högt hjälper
  // ingen. (\\in och \\ldots fanns redan längre ned i tabellen.)
  "\\{": "",
  "\\}": "",
  // Understreck som platshållare för okänd siffra i ett tal ("$2\\_59$").
  // Frågetexterna förklarar notationen med just ordet "understrecket", så
  // uppläsningen använder samma ord: "två understreck fem nio".
  "\\_": " understreck ",
  "\\gcd": " största gemensamma faktorn till ",
  "\\cdot": " gånger ",
  "\\times": " gånger ",
  "\\div": " delat med ",
  "\\neq": " är inte lika med ",
  "\\ne": " är inte lika med ",
  "\\leq": " är mindre än eller lika med ",
  "\\le": " är mindre än eller lika med ",
  "\\geq": " är större än eller lika med ",
  "\\ge": " är större än eller lika med ",
  "\\approx": " är ungefär lika med ",
  "\\pm": " plus minus ",
  "\\infty": " oändligheten ",
  "\\pi": " pi ",
  "\\alpha": " alfa ",
  "\\beta": " beta ",
  "\\gamma": " gamma ",
  "\\delta": " delta ",
  "\\Delta": " delta ",
  "\\theta": " theta ",
  "\\lambda": " lambda ",
  "\\mu": " my ",
  "\\sigma": " sigma ",
  "\\%": " procent ",
  "\\circ": " grader ",
  "\\sin": " sinus ",
  "\\cos": " cosinus ",
  "\\tan": " tangens ",
  "\\log": " logaritmen ",
  "\\ln": " naturliga logaritmen ",
  "\\lg": " tiologaritmen ",
  "\\arcsin": " arcus sinus ",
  "\\arccos": " arcus cosinus ",
  "\\arctan": " arcus tangens ",
  "\\Rightarrow": " vilket ger ",
  "\\Leftrightarrow": " är ekvivalent med ",
  "\\Longleftrightarrow": " är ekvivalent med ",
  "\\to": " går mot ",
  "\\ldots": " och så vidare ",
  "\\cap": " snitt ",
  "\\cup": " union ",
  "\\ell": " l ",
  "\\in": " tillhör ",
  "\\angle": " vinkeln ",
  "\\dots": " och så vidare ", // alias of \ldots
  "\\cdots": " och så vidare ", // alias of \ldots
  "\\arg": " argumentet ",
  "\\omega": " omega ",
  "\\subset": " är en delmängd av ",
  "\\varphi": " fi ",
  // Ma5 kap 4/6 (2026-09-26): Binets formel och DE-notation.
  "\\phi": " fi ",
  "\\psi": " psi ",
  "\\rho": " rå ",
  "\\tau": " tau ",
  "\\varepsilon": " epsilon ",
  "\\eta": " eta ",
  "\\leftrightarrow": " hör ihop med ",
  // Bevis- och talteorinotation (MATF1000X kap 1, 2026-09-25): utan de här
  // raderna föll "$5 \\mid 35$" och "$\\neg P$" tillbaka på "uttrycket".
  "\\mid": " delar ",
  "\\nmid": " delar inte ",
  "\\neg": " inte ",
  "\\notin": " tillhör inte ",
  "\\equiv": " är kongruent med ",
  "\\bmod": " modulo ",
  "\\pmod": " modulo ",
  "\\blacksquare": " vilket skulle visas ",
  // Talbaser och kongruens (MATF1000X kap 2, 2026-09-26).
  "\\lt": " är mindre än ",
  "\\gt": " är större än ",
  "\\setminus": " utom ",
  "\\mapsto": " avbildas på ",
  // Mängdlära (MATF1000X kap 3/5).
  "\\emptyset": " tomma mängden ",
  "\\varnothing": " tomma mängden ",
  "\\subseteq": " är en delmängd av ",
  "\\supseteq": " innehåller ",
  "\\subsetneq": " är en äkta delmängd av ",
  // Logik och sannolikhet (MATF1000X kap 5, 2026-09-26).
  "\\iff": " om och endast om ",
  "\\lor": " eller ",
  "\\land": " och ",
  "\\Omega": " omega ",
  "\\min": " minsta värdet av ",
  // Takfunktionen (MATF1000X kap 7, 2026-09-26): ⌈x⌉ = minsta heltal ≥ x.
  "\\lceil": " talet ",
  "\\rceil": " avrundat uppåt till heltal ",
  "\\max": " största värdet av ",
};

// Commands with genuinely no spoken meaning of their own — read as silence
// (or a bare space, where omitting it would run two tokens together) rather
// than triggering the "uttrycket" fallback. Kept separate from KOMMANDON so
// the two lists read as what they are: "here is the word" vs. "there is
// deliberately no word".
const INGEN_INNEBORD: Record<string, string> = {
  "\\left": "",
  "\\right": "",
  "\\,": " ",
  "\\;": " ",
  "\\:": " ",
  "\\!": "",
  "\\ ": " ",
  "\\quad": " ",
  "\\qquad": " ",
  "\\displaystyle": " ",
  // \mathbb{R} etc. — the command itself is silent; the argument (read via
  // the ordinary "{" handling right after) carries the actual content.
  "\\mathbb": "",
  // \mathbf{v} — bold math variable/vector, same treatment as \mathbb: the
  // command carries no sound of its own, the argument is what matters.
  "\\mathbf": "",
  // \mathcal{P}(A) — the potensmängd; the letter itself is read via the argument.
  "\\mathcal": "",
};

// \big, \Big, \bigg, \Bigg and their \bigl/\bigr/\bigm (+ capitalised)
// siblings — pure delimiter sizing, too many spellings to enumerate above.
const STORLEKSKOMMANDO = /^\\[Bb]igg?[lmr]?$/;

// Fractions a student would name rather than spell out.
const NAMNGIVNA_BRAK: Record<string, string> = {
  "1/2": "en halv",
  "1/3": "en tredjedel",
  "2/3": "två tredjedelar",
  "1/4": "en fjärdedel",
  "3/4": "tre fjärdedelar",
  "1/5": "en femtedel",
  "1/6": "en sjättedel",
  "1/8": "en åttondel",
  "1/10": "en tiondel",
};

// Subscripts read better as words: x_1 -> "x ett".
const SIFFEROD: Record<string, string> = {
  "0": "noll", "1": "ett", "2": "två", "3": "tre", "4": "fyra",
  "5": "fem", "6": "sex", "7": "sju", "8": "åtta", "9": "nio",
};

const FUNKTIONSORD = new Set([
  "sinus", "cosinus", "tangens", "logaritmen", "tiologaritmen", "argumentet",
]);

interface Las {
  text: string;
  i: number;
}

// Reads one argument: a {...} group (inner text returned), a |...| pair
// (absolute value), a \command, or a single character — LaTeX allows all of
// these (\sqrt2, x^2, \frac12, |x-3|).
function lasArgument(s: string, i: number): Las {
  if (s[i] === "{") {
    let djup = 1;
    let j = i + 1;
    while (j < s.length && djup > 0) {
      if (s[j] === "{") djup++;
      else if (s[j] === "}") djup--;
      if (djup > 0) j++;
    }
    return { text: s.slice(i + 1, j), i: j + 1 };
  }
  if (s[i] === "|") {
    const slut = s.indexOf("|", i + 1);
    if (slut === -1) return { text: s.slice(i + 1), i: s.length };
    return { text: s.slice(i + 1, slut), i: slut + 1 };
  }
  if (s[i] === "\\") {
    const m = /^\\[a-zA-Z]+/.exec(s.slice(i));
    if (m) return { text: m[0], i: i + m[0].length };
  }
  return { text: s[i] ?? "", i: i + 1 };
}

function brak(taljare: string, namnare: string): string {
  const namn = NAMNGIVNA_BRAK[`${taljare.trim()}/${namnare.trim()}`];
  if (namn) return namn;
  return `${tolka(taljare)} delat med ${tolka(namnare)}`;
}

function potens(exponent: string): string {
  const e = exponent.trim();
  if (e === "grader") return " grader "; // 90^\circ
  if (e === "2") return " i kvadrat ";
  if (e === "3") return " i kubik ";
  return ` upphöjt till ${e} `;
}

function index(varde: string): string {
  const t = varde.trim();
  return SIFFEROD[t] ?? t;
}

// A lone variable letter (x, t, ...) or a lone number (2, 3.5 — but NOT a
// literal comma like "3,2", since that's how a coordinate's x/y-separator
// is written and must stay indistinguishable from it, see the "known
// limitation" comment further down). Anything else — an expression, more
// than one token — is not "simple".
function enkeltArgument(s: string, oppnandeIndex: number): boolean {
  let djup = 1;
  let j = oppnandeIndex + 1;
  while (j < s.length && djup > 0) {
    if (s[j] === "(") djup++;
    else if (s[j] === ")") djup--;
    if (djup > 0) j++;
  }
  const inre = s.slice(oppnandeIndex + 1, j).trim();
  return /^[a-zA-ZåäöÅÄÖ]$/.test(inre) || /^\d+(\.\d+)?$/.test(inre);
}

function tolka(s: string): string {
  let ut = "";
  let i = 0;
  // Tracks whether each open paren was spoken as "av" (function application)
  // or as "parentes", so the closing paren can match it.
  const parenteser: ("av" | "parentes")[] = [];

  const sistaOrdet = () => {
    const m = /([a-zA-ZåäöÅÄÖ]+)\s*$/.exec(ut);
    return m ? m[1] : "";
  };

  while (i < s.length) {
    const c = s[i];

    if (c === "{") {
      const g = lasArgument(s, i);
      ut += ` ${tolka(g.text)} `;
      i = g.i;
      continue;
    }
    if (c === "}") { i++; continue; }

    if (c === "\\") {
      const m = /^\\[a-zA-Z]+|^\\./.exec(s.slice(i));
      const kmd = m ? m[0] : "\\";

      if (kmd === "\\frac" || kmd === "\\dfrac" || kmd === "\\tfrac") {
        const a = lasArgument(s, i + kmd.length);
        const b = lasArgument(s, a.i);
        ut += ` ${brak(a.text, b.text)} `;
        i = b.i;
        continue;
      }
      if (kmd === "\\int") {
        // Indefinite: "integralen av f(x) dx". Definite: the bounds are read
        // first, the way a teacher says it — "integralen från 0 till 1 av
        // x dx". Only a complete pair of bounds is read; a lone bound has no
        // natural Swedish reading and falls back.
        let j = i + kmd.length;
        while (s[j] === " ") j++;
        if (s.startsWith("\\limits", j)) j += "\\limits".length;
        let fran = "";
        let till = "";
        while (s[j] === "_" || s[j] === "^") {
          const typ = s[j];
          const a = lasArgument(s, j + 1);
          if (typ === "_") fran = tolka(a.text).trim();
          else till = tolka(a.text).trim();
          j = a.i;
        }
        if (!fran !== !till) throw new OkantKommandoError(kmd);
        ut += fran ? ` integralen från ${fran} till ${till} av ` : " integralen av ";
        i = j;
        continue;
      }
      if (kmd === "\\not") {
        // \not negates the relation that follows ("\not\equiv", "\not=").
        // Spoken as the negated relation, never as a stray "inte" before it.
        let j = i + kmd.length;
        while (s[j] === " ") j++;
        const nasta = /^\\[a-zA-Z]+|^./.exec(s.slice(j))?.[0] ?? "";
        const negerad: Record<string, string> = {
          "\\equiv": " är inte kongruent med ",
          "=": " är inte lika med ",
          "\\in": " tillhör inte ",
          "\\mid": " delar inte ",
          "\\subset": " är inte en delmängd av ",
          "\\subseteq": " är inte en delmängd av ",
        };
        if (!negerad[nasta]) throw new OkantKommandoError(kmd + nasta);
        ut += negerad[nasta];
        i = j + nasta.length;
        continue;
      }
      if (kmd === "\\binom" || kmd === "\\dbinom" || kmd === "\\tbinom") {
        // \binom{n}{k} is read the Swedish way: "n över k".
        const a = lasArgument(s, i + kmd.length);
        const b = lasArgument(s, a.i);
        ut += ` ${tolka(a.text)} över ${tolka(b.text)} `;
        i = b.i;
        continue;
      }
      if (kmd === "\\sqrt") {
        let j = i + kmd.length;
        let grad = "";
        if (s[j] === "[") {
          const slut = s.indexOf("]", j);
          grad = s.slice(j + 1, slut);
          j = slut + 1;
        }
        const a = lasArgument(s, j);
        ut += grad.trim() === "3"
          ? ` kubikroten ur ${tolka(a.text)} `
          : ` roten ur ${tolka(a.text)} `;
        i = a.i;
        continue;
      }
      if (kmd === "\\underbrace") {
        // \underbrace{1+2+\cdots+k}_{=\frac{k(k+1)}{2}} marks a part of an
        // expression and states what it equals — read as "…, vilket är lika
        // med …" so the annotation is spoken right after the part it labels.
        const a = lasArgument(s, i + kmd.length);
        let j = a.i;
        let etikett = "";
        if (s[j] === "_") {
          const b = lasArgument(s, j + 1);
          etikett = tolka(b.text);
          j = b.i;
        }
        ut += etikett
          ? ` ${tolka(a.text)}, vilket ${etikett}, `
          : ` ${tolka(a.text)} `;
        i = j;
        continue;
      }
      if (kmd === "\\sum") {
        // \sum_{i=1}^{n} a_i — read the bounds first, then the summand as it
        // follows: "summan, då i är lika med 1 upp till n, av a index i".
        // The summand's right edge is not marked in LaTeX; reading on
        // left-to-right matches how a teacher says it aloud.
        let j = i + kmd.length;
        let fran = "";
        let till = "";
        while (s[j] === "_" || s[j] === "^") {
          const typ = s[j];
          const a = lasArgument(s, j + 1);
          if (typ === "_") fran = tolka(a.text);
          else till = tolka(a.text);
          j = a.i;
        }
        ut += fran || till
          ? ` summan, då ${fran}${till ? ` upp till ${till}` : ""}, av `
          : " summan av ";
        i = j;
        continue;
      }
      if (
        kmd === "\\text" ||
        kmd === "\\mathrm" ||
        kmd === "\\operatorname" ||
        kmd === "\\texttt"
      ) {
        const a = lasArgument(s, i + kmd.length);
        ut += ` ${a.text} `;
        i = a.i;
        continue;
      }
      if (kmd === "\\overline") {
        // \overline{AB} names a line segment ("sträckan AB"); \overline{0,3}
        // marks a repeating decimal — a different reading this file does not
        // attempt. One to three letters disambiguates the segment case
        // cheaply; anything else (digits, longer expressions) falls back to
        // "uttrycket" for the whole expression rather than guessing.
        const a = lasArgument(s, i + kmd.length);
        const bokstaver = a.text.trim();
        if (/^[a-zA-ZåäöÅÄÖ]{1,3}$/.test(bokstaver)) {
          ut += ` sträckan ${bokstaver} `;
          i = a.i;
          continue;
        }
        throw new OkantKommandoError(kmd);
      }
      if (kmd === "\\vec") {
        const a = lasArgument(s, i + kmd.length);
        ut += ` vektorn ${tolka(a.text)} `;
        i = a.i;
        continue;
      }
      if (kmd === "\\bar") {
        const a = lasArgument(s, i + kmd.length);
        ut += ` ${tolka(a.text)}-medelvärdet `;
        i = a.i;
        continue;
      }

      const ingenInnebord = INGEN_INNEBORD[kmd];
      if (ingenInnebord !== undefined) {
        ut += ingenInnebord;
        i += kmd.length;
        continue;
      }
      if (STORLEKSKOMMANDO.test(kmd)) {
        i += kmd.length;
        continue;
      }
      const ord = KOMMANDON[kmd];
      if (ord !== undefined) {
        ut += ord;
        i += kmd.length;
        continue;
      }

      // Unrecognised command — see the file-header comment and
      // OkantKommandoError: fall back for the whole expression rather than
      // silently dropping just this token.
      throw new OkantKommandoError(kmd);
    }

    // Both bounds required — a lone subscript is the residue-class notation
    // [a]_m (kongruensräkning), which keeps its ordinary index reading.
    if (c === "]" && /^\](_(\{[^}]*\}|[^{])\^|\^(\{[^}]*\}|[^{])_)/.test(s.slice(i))) {
      // Evaluation bracket [F(x)]_a^b after integrating: "… beräknat från a
      // till b". Without this the bounds were read as an index and a power.
      let j = i + 1;
      let fran = "";
      let till = "";
      while (s[j] === "_" || s[j] === "^") {
        const typ = s[j];
        const a = lasArgument(s, j + 1);
        if (typ === "_") fran = tolka(a.text).trim();
        else till = tolka(a.text).trim();
        j = a.i;
      }
      ut += ` ] beräknat från ${fran} till ${till} `;
      i = j;
      continue;
    }

    if (c === "|") {
      const a = lasArgument(s, i);
      ut += ` absolutbeloppet av ${tolka(a.text)} `;
      i = a.i;
      continue;
    }

    if (c === "^") {
      const a = lasArgument(s, i + 1);
      ut += potens(tolka(a.text));
      i = a.i;
      continue;
    }
    if (c === "_") {
      const a = lasArgument(s, i + 1);
      ut += ` ${index(tolka(a.text))} `;
      i = a.i;
      continue;
    }

    if (c === "(") {
      const ord = sistaOrdet();
      // "(" means function application ("av") when either:
      //  1. the preceding word is a known function word (sinus/cosinus/...)
      //     — the argument's shape doesn't matter here, \sin(2x) is still
      //     "sinus av 2x";
      //  2. the preceding token is a single letter AND the parenthesis
      //     holds exactly one simple argument (one variable letter or one
      //     number) — this is what makes K(x), N(t), f(2) read as function
      //     calls while x(x-3) and a(b+c) (not a single simple argument)
      //     fall back to the neutral "parentes" reading.
      const somFunktion =
        FUNKTIONSORD.has(ord) ||
        (ord.length === 1 && enkeltArgument(s, i));
      // Two parenthesised groups written back to back, e.g. (x+2)(x-3) or
      // (x+2) (x-3), are implicitly multiplied — say so. Whitespace between
      // the groups is skipped when looking for the preceding ")". Note the
      // asymmetry: "3(x+2)" keeps its bare "parentes" reading (no "gånger"),
      // since only ")(" adjacency triggers this, not "number before group".
      let k = i - 1;
      while (k >= 0 && /\s/.test(s[k])) k--;
      if (k >= 0 && s[k] === ")") {
        ut += " gånger ";
      }
      parenteser.push(somFunktion ? "av" : "parentes");
      ut += somFunktion ? " av " : " parentes ";
      i++;
      continue;
    }
    if (c === ")") {
      ut += parenteser.pop() === "parentes" ? " slut parentes " : " ";
      i++;
      continue;
    }

    if (c === "+") { ut += " plus "; i++; continue; }
    if (c === "-") { ut += " minus "; i++; continue; }
    if (c === "=") { ut += " är lika med "; i++; continue; }
    if (c === "<") { ut += " är mindre än "; i++; continue; }
    if (c === ">") { ut += " är större än "; i++; continue; }
    if (c === "/") { ut += " delat med "; i++; continue; }
    if (c === "*") { ut += " gånger "; i++; continue; }
    if (c === "&") { ut += " "; i++; continue; }

    // Numbers: LaTeX is written 3.5 but Swedish speech synthesis wants 3,5.
    // Known limitation: this makes a coordinate point like A(3,2) read as
    // "A parentes 3,2 slut parentes" — indistinguishable from the decimal
    // 3,2. Genuinely ambiguous in Swedish notation; not resolved here.
    if (/\d/.test(c)) {
      const m = /^\d+(?:[.,]\d+)?/.exec(s.slice(i));
      const tal = m ? m[0] : c;
      ut += tal.replace(".", ",");
      i += tal.length;
      continue;
    }

    ut += c;
    i++;
  }

  return ut;
}

function stada(s: string): string {
  return s
    .replace(/\s+/g, " ")
    .replace(/\s+([,.:;])/g, "$1")
    .trim();
}

// A written algorithm (uppställning) set as \begin{array}…\end{array} is read
// row by row, the way a teacher reads it off the board: cells in a row are
// spoken in order, rows are separated by a comma and \hline is "streck".
// Only the array environment is unwrapped; every other \begin stays in
// EJ_STODD. (MATF1000X kap 2, aritmetik i andra baser.)
function lasUppstallning(latex: string): string {
  return latex.replace(
    /\\begin\{array\}\{[^}]*\}([\s\S]*?)\\end\{array\}/g,
    (_m, kropp: string) =>
      " " +
      kropp
        .split(/\\\\/)
        .flatMap((rad) => {
          const delar = rad.split(/\\hline/);
          return delar.flatMap((d, k) => {
            const celler = d
              .split("&")
              .map((c) => c.replace(/\\scriptstyle/g, "").trim())
              .filter(Boolean)
              .join(" ");
            return k > 0 ? ["\\text{streck}", celler] : [celler];
          });
        })
        .filter(Boolean)
        .join(", ") +
      " "
  );
}

export function latexTillTal(latex: string): string {
  if (!latex.trim()) return "";
  latex = lasUppstallning(latex);
  // "\{\}" (an empty listing) is the empty set — the braces themselves are
  // silent, so without this the whole formula read as nothing → "uttrycket".
  latex = latex.replace(/\\\{\s*\\\}/g, "\\emptyset ");
  if (EJ_STODD.test(latex)) return FALLBACK;
  try {
    const talat = stada(tolka(latex));
    return talat || FALLBACK;
  } catch (e) {
    if (e instanceof OkantKommandoError) return FALLBACK;
    throw e;
  }
}
